import { zodResolver } from '@hookform/resolvers/zod';
import { useRouteLoaderData } from '@remix-run/react';
import { ArrowRight, ChevronDown, Phone, ShieldCheck, X } from 'lucide-react';
import { useState } from 'react';
import { Alert, Button, Collapse, Form, Modal, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useFlashMessages } from '~/context/FlashMessagesContext';
import type { loader as rootLoader } from '~/root';
import { apiFetch } from '~/utils/apiFetch';
import { useFormatPrice } from '~/utils/price';

// Endpoint contract — implemented separately by the backend team.
// Emails the inquiry (incl. the product snapshot below) to prodej@anderskafka.cz.
const INQUIRY_ENDPOINT = '/api/v2/shop/product-inquiries';

export interface InquiryProductContext {
  productName: string;
  productCode: string;
  variantCode?: string;
  selectedOptions: { name: string; value: string }[];
  selectedCustomizations: { id: number; label: string; price: number | null }[];
  attributes: { name: string; value: string }[];
  basePriceFormatted: string; // selected variant without the configuration
  totalPrice: number; // raw integer (minor units), basePrice + additionalPrice
  totalPriceFormatted: string;
  currency?: string;
}

const inquirySchema = z.object({
  name: z.string().min(1, 'components.form.validation.required'),
  email: z.string().min(1, 'components.form.validation.required').email('components.form.validation.email_invalid'),
  phone: z.string().min(1, 'components.form.validation.required'),
  place: z.string().min(1, 'components.form.validation.required'),
  note: z.string().optional(),
  consent: z.boolean().refine((v) => v === true, {
    message: 'components.product_inquiry.consent_required',
  }),
});

type InquiryFormData = z.infer<typeof inquirySchema>;

interface Props {
  show: boolean;
  onHide: () => void;
  product: InquiryProductContext;
}

const getApiUrl = (): string => {
  if (typeof window !== 'undefined' && window.ENV?.API_URL) {
    return window.ENV.API_URL;
  }
  return '';
};

const ProductInquiryModal = ({ show, onHide, product }: Props) => {
  const { t } = useTranslation();
  const { addMessage } = useFlashMessages();
  const formatPrice = useFormatPrice();
  const rootData = useRouteLoaderData<typeof rootLoader>('root');

  const [serverError, setServerError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const storePhone = rootData?.storePhone;
  const phoneHref = storePhone ? `tel:${storePhone.replace(/\s+/g, '')}` : undefined;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<InquiryFormData>({
    resolver: zodResolver(inquirySchema),
    defaultValues: { name: '', email: '', phone: '', place: '', note: '', consent: false },
  });

  const onSubmit = async (data: InquiryFormData) => {
    setIsLoading(true);
    setServerError(null);

    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) throw new Error(t('common.errors.api_not_configured'));

      const response = await apiFetch(`${apiUrl}${INQUIRY_ENDPOINT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          place: data.place,
          note: data.note || undefined,
          consent: data.consent,
          // The backend resolves name, options, price & currency server-side from
          // the product/variant code — it only reads the fields below.
          product: {
            code: product.productCode,
            variantCode: product.variantCode,
            selectedCustomizations: product.selectedCustomizations.map((c) => ({ id: c.id })),
            attributes: product.attributes,
          },
        }),
      });

      if (!response.ok) {
        let message = t('components.product_inquiry.error');
        try {
          const body = await response.json();
          message = body.violations?.[0]?.message || body['hydra:description'] || body.detail || message;
        } catch {
          /* keep default message */
        }
        throw new Error(message);
      }

      addMessage('success', t('components.product_inquiry.success'));
      reset();
      onHide();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : t('components.product_inquiry.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setServerError(null);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="product-inquiry__dialog"
      contentClassName="product-inquiry"
      aria-labelledby="product-inquiry-title"
    >
      <div className="product-inquiry__header">
        <button type="button" className="product-inquiry__close" onClick={handleClose} aria-label={t('common.close')}>
          <X size={20} />
        </button>
        <h2 id="product-inquiry-title" className="product-inquiry__title">
          {t('components.product_inquiry.title')}
        </h2>
        <p className="product-inquiry__subtitle">{t('components.product_inquiry.subtitle')}</p>
      </div>

      <div className="product-inquiry__body">
        <div className="product-inquiry__aside">
          {phoneHref && (
            <a href={phoneHref} className="product-inquiry__phone">
              <span className="product-inquiry__phone-icon">
                <Phone size={16} />
              </span>
              <span className="product-inquiry__phone-label">{t('components.product_inquiry.call_label')}</span>
              <span className="product-inquiry__phone-number">{storePhone}</span>
            </a>
          )}

          <div className={`product-inquiry__summary${summaryOpen ? ' is-open' : ''}`}>
            <div className="product-inquiry__summary-body">
              <span className="product-inquiry__summary-title">{t('components.product_inquiry.your_product')}</span>
              <div className="product-inquiry__summary-name">{product.productName}</div>
              {product.selectedOptions.length > 0 && (
                <ul className="product-inquiry__summary-list">
                  {product.selectedOptions.map((opt) => (
                    <li key={`opt-${opt.name}`}>
                      <span>{opt.name}</span>
                      <span>{opt.value}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="product-inquiry__summary-price">
                <span>{t('components.product_inquiry.total_price')}</span>
                <span>{product.totalPriceFormatted}</span>
              </div>
            </div>
            {product.selectedCustomizations.length > 0 && (
              <>
                <button
                  type="button"
                  className="product-inquiry__summary-toggle"
                  onClick={() => setSummaryOpen((open) => !open)}
                  aria-expanded={summaryOpen}
                  aria-controls="product-inquiry-summary-configuration"
                >
                  <span className="product-inquiry__summary-title">
                    {t('components.product_inquiry.configuration', { count: product.selectedCustomizations.length })}
                  </span>
                  <ChevronDown size={20} className="product-inquiry__summary-chevron" />
                </button>
                <Collapse in={summaryOpen}>
                  <div id="product-inquiry-summary-configuration">
                    <div className="product-inquiry__summary-body product-inquiry__summary-body--configuration">
                      <ul className="product-inquiry__summary-list">
                        <li>
                          <span>{t('components.product_inquiry.base_price')}</span>
                          <span>{product.basePriceFormatted}</span>
                        </li>
                        {product.selectedCustomizations.map((cust, i) => (
                          <li key={`cust-${i}`}>
                            <span>{cust.label}</span>
                            <span>
                              {cust.price !== null
                                ? `${cust.price > 0 ? '+ ' : ''}${formatPrice(cust.price, product.currency)}`
                                : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Collapse>
              </>
            )}
          </div>
        </div>

        <Form
          className="product-inquiry__form"
          onSubmit={(e) => {
            e.stopPropagation();
            handleSubmit(onSubmit)(e);
          }}
          noValidate
        >
          {serverError && (
            <Alert variant="danger" className="product-inquiry__field--full mb-0">
              <strong>{t('common.error')}</strong>
              <div>{serverError}</div>
            </Alert>
          )}

          <Form.Group className="product-inquiry__field product-inquiry__field--full" controlId="inquiryName">
            <Form.Label>
              {t('components.product_inquiry.name')} <span className="product-inquiry__required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={t('components.product_inquiry.name_placeholder')}
              autoComplete="name"
              isInvalid={!!errors.name}
              {...register('name')}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message ? t(errors.name.message) : ''}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="product-inquiry__field" controlId="inquiryEmail">
            <Form.Label>
              {t('components.product_inquiry.email')} <span className="product-inquiry__required">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              placeholder={t('components.product_inquiry.email_placeholder')}
              autoComplete="email"
              isInvalid={!!errors.email}
              {...register('email')}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email?.message ? t(errors.email.message) : ''}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="product-inquiry__field" controlId="inquiryPhone">
            <Form.Label>
              {t('components.product_inquiry.phone')} <span className="product-inquiry__required">*</span>
            </Form.Label>
            <Form.Control
              type="tel"
              placeholder={t('components.product_inquiry.phone_placeholder')}
              autoComplete="tel"
              isInvalid={!!errors.phone}
              {...register('phone')}
            />
            <Form.Control.Feedback type="invalid">
              {errors.phone?.message ? t(errors.phone.message) : ''}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="product-inquiry__field product-inquiry__field--full" controlId="inquiryPlace">
            <Form.Label>
              {t('components.product_inquiry.place')} <span className="product-inquiry__required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={t('components.product_inquiry.place_placeholder')}
              isInvalid={!!errors.place}
              {...register('place')}
            />
            <Form.Control.Feedback type="invalid">
              {errors.place?.message ? t(errors.place.message) : ''}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="product-inquiry__field product-inquiry__field--full" controlId="inquiryNote">
            <Form.Label>{t('components.product_inquiry.note')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder={t('components.product_inquiry.note_placeholder')}
              {...register('note')}
            />
          </Form.Group>

          <Form.Group className="product-inquiry__consent" controlId="inquiryConsent">
            <Form.Check
              type="checkbox"
              label={t('components.product_inquiry.consent')}
              isInvalid={!!errors.consent}
              feedback={errors.consent?.message ? t(errors.consent.message) : undefined}
              feedbackType="invalid"
              {...register('consent')}
            />
          </Form.Group>

          <div className="product-inquiry__submit-wrap">
            <Button type="submit" className="product-inquiry__submit" disabled={isLoading} aria-busy={isLoading}>
              {isLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  {t('components.product_inquiry.submitting')}
                </>
              ) : (
                <>
                  {t('components.product_inquiry.submit')}
                  <ArrowRight size={20} className="ms-2" />
                </>
              )}
            </Button>
            <p className="product-inquiry__trust">
              <ShieldCheck size={14} />
              {t('components.product_inquiry.trust')}
            </p>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default ProductInquiryModal;
